import * as React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import theme, { Theme } from 'helpers/theme';
import { createFragmentContainer, graphql } from 'react-relay';
import { useRouter } from 'found';
import PageTitle from 'components/PageTitle';
import Table, { RowCell } from 'components/Table';
import moment from 'moment';
import CheckStatus from 'features/background-checks/components/CheckStatus';
import {
  getStatus,
  statusText
} from 'features/background-checks/utils/helpers';
import {
  historyType,
  getHistoryName,
  calculateHistoryGaps
} from './components/helpers';
import Timeline from './components/Timeline';
import DataItem from 'features/user-profile/components/DataItem';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faEnvelope,
  faClipboardList,
  faShieldCheck,
  faCalendarDays,
  faIdCard,
  faUserShield,
  faExclamationTriangle,
  faClock,
  faFileText,
  faCalendarXmark
} from '@fortawesome/pro-duotone-svg-icons';
import {
  BackgroundCheckDetail_backgroundCheck,
  BackgroundCheckDetail_backgroundCheck$data
} from './__generated__/BackgroundCheckDetail_backgroundCheck.graphql';
import Card from 'features/user-profile/components/Card';
import Button from 'components/Button/Button';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import Tooltip from 'components/Tooltip';
import Spacer from 'sharedComponents/core/Spacers/Spacer';

const useStyles = createUseStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: '40px 40px'
  },
  backButton: {
    color: theme.colors.primaryBlue,
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    marginBottom: 20
  },
  section: {
    marginBottom: 40,
    flex: 1,
    maxWidth: '400px'
  },
  rightSection: {
    flex: 1
  },
  personalInfo: {},
  infoItems: {
    gap: '2rem',
    display: 'flex',
    flexWrap: 'wrap'
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0
  },
  infoItem__label: {
    color: '#6A728F',
    margin: 0,
    padding: 0,
    fontSize: '0.8rem'
  },
  infoItem__text: {
    margin: 0,
    padding: 0,
    fontSize: '0.9rem'
  },
  subtitle: {
    fontSize: '0.9rem',
    fontWeight: 600
  },
  bgItems: {
    display: 'flex',
    gap: '10px',
    marginTop: 10,
    flexWrap: 'wrap'
  },
  statusContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 10
  },
  timelineSection: {
    flex: 2,
    marginBottom: 30
  },
  issueItems: {
    display: 'flex',
    gap: '10px',
    marginTop: 15,
    flexWrap: 'wrap'
  },
  topSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: '20px',
    marginBottom: 20
  },
  historyTableSection: {
    marginTop: 0
  },
  rowText: {
    fontSize: 13,
    fontWeight: 400,
    textWrap: 'nowrap'
  },
  rowTextOrgType: {
    fontSize: 13,
    fontWeight: 500,
    color: '#6A728F',
    marginBottom: 5
  },
  rowTextOrgName: {
    fontSize: 14,
    fontWeight: 700,
    color: '#1C1C1C',
    marginBottom: 5
  },
  organization: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5
  }
}));

type Props = {
  backgroundCheck: BackgroundCheckDetail_backgroundCheck;
};

const BackgroundCheckDetail = ({ backgroundCheck }: Props) => {
  const theme = useTheme();
  const classes = useStyles({ theme });
  const { router } = useRouter();

  const status = getStatus(backgroundCheck);

  console.log('backgroundCheck', backgroundCheck); // Debugging log

  // Function to calculate outstanding issues
  const getOutstandingIssues = () => {
    const issues: {
      id: string;
      label: string;
      icon: any;
      color: string;
      helpText: string;
      description?: string;
    }[] = [];

    const activeHistory =
      backgroundCheck.history?.filter((item) => !item.isArchived) || [];

    // Check for application not received
    if (
      !backgroundCheck.applicationReceivedAt &&
      !backgroundCheck.isVerification &&
      !backgroundCheck.onlyDBS
    ) {
      issues.push({
        id: 'app-not-received',
        label: 'Application not received',
        icon: faFileText,
        color: '#f68f1f',
        helpText: 'The background check application has not been received yet'
      });
    }

    // Check for DBS waiting
    if (backgroundCheck.requestedDBS && !backgroundCheck.dbsExpiryDate) {
      issues.push({
        id: 'dbs-waiting',
        label: 'DBS check pending',
        icon: faUserShield,
        color: '#f68f1f',
        helpText: 'Waiting for DBS check to be completed'
      });
    }

    // Check for ID expiry
    if (
      !backgroundCheck.isVerification &&
      !backgroundCheck.idExpiryDate &&
      !backgroundCheck.onlyDBS
    ) {
      issues.push({
        id: 'id-expiry',
        label: 'ID document pending',
        icon: faIdCard,
        color: '#f68f1f',
        helpText: 'ID document has not been given or is pending verification'
      });
    }

    // Check for responses waiting
    const responsesWaiting = activeHistory.filter((item) => {
      const hasResponse =
        item?.employer?.response ||
        item?.education?.response ||
        item?.selfEmployed?.response ||
        item?.unemployed?.response;
      return !hasResponse;
    });

    if (responsesWaiting.length > 0) {
      issues.push({
        id: 'responses-waiting',
        label: `${responsesWaiting.length} response${
          responsesWaiting.length > 1 ? 's' : ''
        } pending`,
        icon: faClock,
        color: '#f68f1f',
        helpText: `Waiting for ${responsesWaiting.length} organization${
          responsesWaiting.length > 1 ? 's' : ''
        } to respond`
      });
    }

    // Check for gaps over 28 days using the Timeline calculation
    if (backgroundCheck.applicationReceivedAt && activeHistory.length > 0) {
      const timelineStart = moment(backgroundCheck.applicationReceivedAt);
      const yearsBack = backgroundCheck.isCAA ? 5 : 3;

      const { hasLargeGaps } = calculateHistoryGaps(
        backgroundCheck.history || [],
        timelineStart,
        yearsBack
      );

      if (hasLargeGaps) {
        issues.push({
          id: 'large-gaps',
          label: 'Employment gaps over 28 Days',
          icon: faCalendarXmark,
          color: '#f68f1f',
          helpText: 'There are gaps in employment history longer than 28 days'
        });
      }
    }

    if (
      backgroundCheck.isCAA &&
      !backgroundCheck.isVerification &&
      !backgroundCheck.isRenewal &&
      !backgroundCheck.interviewNotes &&
      !backgroundCheck.approvalDate &&
      !backgroundCheck.onlyDBS
    ) {
      // Check for interview notes needed
      const lastInterviewNotesLog =
        backgroundCheck.interviewNotesSendLogs?.[
          backgroundCheck.interviewNotesSendLogs.length - 1
        ];
      const interviewNotesLastSent = lastInterviewNotesLog
        ? moment(lastInterviewNotesLog.sentAt).fromNow()
        : undefined;

      issues.push({
        id: 'interview-notes-needed',
        label: 'Interview notes required',
        description: lastInterviewNotesLog
          ? `Sent to ${lastInterviewNotesLog.emailAddress}`
          : undefined,
        icon: faClipboardList,
        color: '#f68f1f',
        helpText: interviewNotesLastSent
          ? `Last sent: ${interviewNotesLastSent}`
          : 'Not sent yet'
      });
    }

    return issues;
  };

  const outstandingIssues = getOutstandingIssues();

  // Extract date matching logic into a reusable function
  const getDateMatchStatus = (
    item: any
  ): {
    status: 'no-response' | 'verified' | 'close-match' | 'mismatch';
    color: string;
    label: string;
    description: string;
  } => {
    const applicationDates =
      item?.employer?.application?.dates ??
      item?.education?.application?.dates ??
      item?.selfEmployed?.application?.dates ??
      item?.unemployed?.application?.dates ??
      [];

    const responseDates =
      item?.employer?.response?.datesEmployed ??
      item?.education?.response?.dates ??
      item?.selfEmployed?.response?.dates ??
      item?.unemployed?.response?.alternateDates ??
      null;

    if (
      applicationDates.length === 0 ||
      !responseDates ||
      responseDates.length === 0
    ) {
      return {
        status: 'no-response',
        color: '#f68f1f',
        label: 'Response not received',
        description: 'Waiting for response'
      };
    }

    const appStartDate = moment(applicationDates[0].startDate).format(
      'DD/MM/YYYY'
    );
    const appEndDate = moment(applicationDates[0].endDate).format('DD/MM/YYYY');
    const respStartDate = moment(responseDates[0].startDate).format(
      'DD/MM/YYYY'
    );
    const respEndDate = moment(responseDates[0].endDate).format('DD/MM/YYYY');

    const datesMatch =
      appStartDate === respStartDate && appEndDate === respEndDate;
    const isUnemployedAccurate =
      item?.unemployed?.response?.datesAccurate === true;

    // Check if dates are within 10 days of each other
    const appStart = moment(applicationDates[0].startDate);
    const appEnd = moment(applicationDates[0].endDate);
    const respStart = moment(responseDates[0].startDate);
    const respEnd = moment(responseDates[0].endDate);

    const startDiffDays = Math.abs(appStart.diff(respStart, 'days'));
    const endDiffDays = Math.abs(appEnd.diff(respEnd, 'days'));
    const isCloseMatch =
      startDiffDays <= 10 && endDiffDays <= 10 && !datesMatch;

    if (datesMatch || isUnemployedAccurate) {
      return {
        status: 'verified',
        color: 'rgb(23 165 29)',
        label: 'Verified',
        description: 'Response dates match the application dates'
      };
    } else if (isCloseMatch) {
      return {
        status: 'close-match',
        color: '#1fa7d4',
        label: 'Close match',
        description: 'Response dates are close match'
      };
    } else {
      return {
        status: 'mismatch',
        color: '#f68f1f',
        label: 'Date mismatch',
        description: "Response dates don't match"
      };
    }
  };

  const formatHistoryRow: RowCell<any>[] = [
    {
      header: 'Organization',
      width: '30%',
      format: (item) => {
        return (
          <div className={classes.organization}>
            <div className={classes.rowTextOrgName}>{getHistoryName(item)}</div>
            <div className={classes.rowTextOrgType}>
              {historyType(item) === 'employment'
                ? 'Employment'
                : historyType(item) === 'education'
                ? 'Education'
                : historyType(item) === 'self-employment'
                ? 'Self Employment'
                : historyType(item) === 'unemployed'
                ? 'Unemployed'
                : 'Unknown'}
            </div>
          </div>
        );
      }
    },
    {
      header: 'Dates',
      format: (item) => {
        const applicationDates =
          item?.employer?.application?.dates ??
          item?.education?.application?.dates ??
          item?.selfEmployed?.application?.dates ??
          item?.unemployed?.application?.dates ??
          [];

        const responseDates =
          item?.employer?.response?.datesEmployed ??
          item?.education?.response?.dates ??
          item?.selfEmployed?.response?.dates ??
          item?.unemployed?.response?.alternateDates ??
          null;

        if (applicationDates.length > 0) {
          const appStartDate = moment(applicationDates[0].startDate).format(
            'DD/MM/YYYY'
          );
          const appEndDate = moment(applicationDates[0].endDate).format(
            'DD/MM/YYYY'
          );

          let dateDisplay = (
            <div className={classes.rowText}>
              <div
                style={{
                  color: '#6A728F',
                  fontSize: '0.75rem',
                  marginBottom: '2px'
                }}
              >
                Application: {appStartDate} - {appEndDate}
              </div>
            </div>
          );

          if (responseDates && responseDates.length > 0) {
            const respStartDate = moment(responseDates[0].startDate).format(
              'DD/MM/YYYY'
            );
            const respEndDate = moment(responseDates[0].endDate).format(
              'DD/MM/YYYY'
            );

            const matchStatus = getDateMatchStatus(item);

            dateDisplay = (
              <div className={classes.rowText}>
                <div
                  style={{
                    color: '#6A728F',
                    fontSize: '0.75rem',
                    marginBottom: '2px'
                  }}
                >
                  Application: {appStartDate} - {appEndDate}
                </div>
                <div
                  style={{
                    color: matchStatus.color,
                    fontSize: '0.75rem',
                    fontWeight: 500
                  }}
                >
                  Response: {respStartDate} - {respEndDate}
                </div>
              </div>
            );
          }

          return dateDisplay;
        }
        return <div className={classes.rowText}>-</div>;
      }
    },
    {
      header: 'Status',
      format: (item) => {
        const hasResponse =
          item?.employer?.response ??
          item?.education?.response ??
          item?.selfEmployed?.response ??
          item?.unemployed?.response;

        if (!hasResponse) {
          return (
            <Tooltip className={classes.rowText} text="No response received">
              <span style={{ color: '#f68f1f', fontWeight: 500 }}>
                Awaiting response
              </span>
            </Tooltip>
          );
        }

        const matchStatus = getDateMatchStatus(item);

        return (
          <Tooltip className={classes.rowText} text={matchStatus.description}>
            <span style={{ color: matchStatus.color, fontWeight: 500 }}>
              {matchStatus.label}
            </span>
          </Tooltip>
        );
      }
    },
    {
      header: 'Contact',
      format: (
        item: NonNullable<
          BackgroundCheckDetail_backgroundCheck$data['history']
        >[number]
      ) => {
        return (
          <div className={classes.rowText}>
            <div
              style={{
                color: '#6A728F',
                fontSize: '0.7rem',
                marginTop: '2px'
              }}
            >
              {item.responseSendLogs && item.responseSendLogs.length > 1 ? (
                <Tooltip
                  style={{ fontSize: '0.7rem', marginTop: '2px' }}
                  text={'Response sent logs'}
                >
                  Sent to:
                  {item.responseSendLogs.map((log: any, index: number) => (
                    <div key={index}>
                      {log.emailAddress} - {moment(log.sentAt).fromNow()}
                    </div>
                  ))}
                </Tooltip>
              ) : (
                '-'
              )}
            </div>
          </div>
        );
      }
    }
  ];

  return (
    <div className={classes.root}>
      <div
        className={classes.backButton}
        onClick={() => router.push('/app/background-checks')}
      >
        <Button archetype="flat" onClick={() => router.go(-1)}>
          <FontAwesomeIcon icon={faChevronLeft} /> Back to checks
        </Button>
      </div>

      <div className={classes.topSection}>
        <div className={classes.section}>
          <Card>
            <div className={classes.personalInfo}>
              {/* <p className={classes.subtitle}>Personal Information</p> */}

              <div className={classes.infoItems}>
                <div className={classes.infoItem}>
                  <p className={classes.infoItem__label}>Full Name</p>
                  <p className={classes.infoItem__text}>
                    {backgroundCheck.delegate.firstName}{' '}
                    {backgroundCheck.delegate.lastName}
                  </p>
                </div>
                <div className={classes.infoItem}>
                  <p className={classes.infoItem__label}>Email Address</p>
                  <p className={classes.infoItem__text}>
                    {backgroundCheck.delegate.email}
                  </p>
                </div>
                <div className={classes.infoItem}>
                  <p className={classes.infoItem__label}>Check Type</p>
                  <p className={classes.infoItem__text}>
                    {backgroundCheck.isRenewal
                      ? 'Renewal'
                      : backgroundCheck.isVerification
                      ? 'Verification'
                      : backgroundCheck.onlyDBS
                      ? 'DBS Check'
                      : 'Initial'}
                  </p>
                </div>
                <div className={classes.infoItem}>
                  <p className={classes.infoItem__label}>Check Authority</p>
                  <p className={classes.infoItem__text}>
                    {backgroundCheck.isCAA ? 'CAA' : '-'}
                  </p>
                </div>
                <div className={classes.infoItem}>
                  <p className={classes.infoItem__label}>Application Sent</p>
                  <p className={classes.infoItem__text}>
                    {backgroundCheck.applicationSentAt
                      ? moment(backgroundCheck.applicationSentAt).format(
                          'DD/MM/YYYY'
                        )
                      : backgroundCheck.onlyDBS
                      ? '-'
                      : 'Not sent'}
                  </p>
                </div>

                <div className={classes.infoItem}>
                  <p className={classes.infoItem__label}>
                    Application Received
                  </p>
                  <p className={classes.infoItem__text}>
                    {backgroundCheck.applicationReceivedAt
                      ? moment(backgroundCheck.applicationReceivedAt).format(
                          'DD/MM/YYYY'
                        )
                      : '-'}
                  </p>
                </div>

                {backgroundCheck.approvalDate && (
                  <div className={classes.infoItem}>
                    <p className={classes.infoItem__label}>Approval Date</p>
                    <p className={classes.infoItem__text}>
                      {moment(backgroundCheck.approvalDate).format(
                        'DD/MM/YYYY'
                      )}
                    </p>
                  </div>
                )}
              </div>
            </div>
            <Spacer spacing={2} vertical />
            <div className={classes.personalInfo}>
              <p className={classes.subtitle}>Background Check Status</p>
              <div
                className={classes.statusContainer}
                style={{ marginTop: 10 }}
              >
                <CheckStatus status={status} />
                {/* <span style={{ marginLeft: 10, fontSize: '0.9rem' }}>
                  {statusText[status]}
                </span> */}
              </div>
            </div>
          </Card>
        </div>

        <div className={classes.rightSection}>
          {!backgroundCheck.onlyDBS && (
            <div className={classes.timelineSection}>
              <Card title="Employment History Timeline">
                {backgroundCheck.history &&
                backgroundCheck.history.filter((item) => !item.isArchived)
                  .length > 0 &&
                backgroundCheck.applicationReceivedAt ? (
                  <Timeline
                    history={backgroundCheck.history.filter(
                      (item) => !item.isArchived
                    )}
                    timelineStartDate={moment(
                      backgroundCheck.applicationReceivedAt
                    )}
                    yearsBack={backgroundCheck.isCAA ? 5 : 3}
                  />
                ) : (
                  <div className={classes.rowText}>Timeline not available</div>
                )}
              </Card>
            </div>
          )}
          {/* Outstanding Issues Section */}
          <div>
            <Card title="Outstanding Issues">
              <div className={classes.issueItems}>
                {outstandingIssues.map((issue) => (
                  <DataItem
                    key={issue.id}
                    icon={issue.icon}
                    iconColor={issue.color}
                    label={issue?.description ?? ''}
                    helpText={issue.helpText}
                    style={{ width: 'calc(50% - 5px)' }}
                  >
                    {issue.label}
                  </DataItem>
                ))}
                {outstandingIssues.length === 0 && (
                  <div>
                    <div className={classes.rowText}>No outstanding issues</div>
                    <div
                      style={{
                        color: '#6A728F',
                        fontSize: '0.8rem',
                        fontStyle: 'italic',
                        marginTop: 10
                      }}
                    >
                      Note: other issues not shown here may still delay checks
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>

      {backgroundCheck.history &&
        backgroundCheck.history.filter((item) => !item.isArchived).length >
          0 && (
          <div className={classes.historyTableSection}>
            <Table
              onSearch={() => {}}
              data={backgroundCheck.history.filter((item) => !item.isArchived)}
              formatRow={formatHistoryRow}
              tableName="background check history"
              emptyTableText="No background check history available"
            />
          </div>
        )}
    </div>
  );
};

const BackgroundCheckDetailFrag = createFragmentContainer(
  BackgroundCheckDetail,
  {
    backgroundCheck: graphql`
      fragment BackgroundCheckDetail_backgroundCheck on BackgroundCheck {
        uuid
        createdAt
        approvalDate
        expiresAt
        applicationSentAt
        applicationReceivedAt
        requestedDBS
        onlyDBS
        hasAllResponses
        idExpiryDate
        dbsExpiryDate
        isRenewal
        isVerification
        isCAA
        interviewNotes {
          yourName
          employmentDecider
        }
        interviewNotesSendLogs {
          sentAt
          emailAddress
        }
        delegate {
          uuid
          firstName
          lastName
          email
        }
        history {
          ...Timeline_history
          uuid
          isArchived
          responseSendLogs {
            emailAddress
            sentAt
          }
          employer {
            uuid
            application {
              uuid
              name
              contactEmail
              jobTitle
              dates {
                startDate
                endDate
              }
            }
            response {
              uuid
              orgName
              orgContactName
              datesEmployed {
                startDate
                endDate
              }
            }
          }
          education {
            uuid
            application {
              uuid
              name
              contactEmail
              dates {
                startDate
                endDate
              }
            }
            response {
              uuid
              orgName
              orgContactName
              dates {
                startDate
                endDate
              }
            }
          }
          selfEmployed {
            uuid
            application {
              uuid
              accountantName
              contactEmail
              dates {
                startDate
                endDate
              }
            }
            response {
              uuid
              orgName
              orgContactName
              dates {
                startDate
                endDate
              }
            }
          }
          unemployed {
            uuid
            application {
              uuid
              dates {
                startDate
                endDate
              }
            }
            response {
              uuid
              alternateDates {
                startDate
                endDate
              }
            }
          }
        }
      }
    `
  }
);

export default BackgroundCheckDetailFrag;
